import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebase/config';

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingCode, setValidatingCode] = useState(true);
  const [validCode, setValidCode] = useState(false);
  const [email, setEmail] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Obtener el código de restablecimiento de la URL
  const queryParams = new URLSearchParams(location.search);
  const oobCode = queryParams.get('oobCode');
  
  // Validar el código de restablecimiento al cargar la página
  useEffect(() => {
    const validateResetCode = async () => {
      if (!oobCode) {
        setError('Enlace de restablecimiento no válido o expirado');
        setValidatingCode(false);
        return;
      }
      
      try {
        // Verificar el código de restablecimiento con Firebase
        const email = await verifyPasswordResetCode(auth, oobCode);
        setEmail(email);
        setValidCode(true);
      } catch (err: any) {
        console.error('Error al validar el código de restablecimiento:', err);
        setError('El enlace de restablecimiento no es válido o ha expirado. Por favor solicita un nuevo enlace.');
        setValidCode(false);
      } finally {
        setValidatingCode(false);
      }
    };
    
    validateResetCode();
  }, [oobCode]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!oobCode) {
      setError('Enlace de restablecimiento no válido');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    
    // Validar fortaleza de la contraseña
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    // Verificar que la contraseña tenga al menos una letra y un número
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
    if (!passwordRegex.test(password)) {
      setError('La contraseña debe contener al menos una letra y un número');
      return;
    }
    
    try {
      setError('');
      setMessage('');
      setLoading(true);
      
      // Confirmar el restablecimiento de la contraseña con Firebase
      await confirmPasswordReset(auth, oobCode, password);
      
      setMessage('Tu contraseña ha sido restablecida exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.');
      
      // Redireccionar al login después de 3 segundos
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      console.error('Error al restablecer la contraseña:', err);
      setError('Error al restablecer la contraseña: ' + (err.message || 'Inténtalo de nuevo'));
    } finally {
      setLoading(false);
    }
  };
  
  // Mostrar un indicador de carga mientras se valida el código
  if (validatingCode) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-domired-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Verificando enlace de restablecimiento...</p>
        </div>
      </div>
    );
  }
  
  // Si el código no es válido, mostrar un mensaje de error
  if (!validCode) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
          <h2 className="text-2xl font-bold text-center mb-6 text-domired-600">Error de Restablecimiento</h2>
          
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
          
          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm font-medium text-domired-600 hover:text-domired-500">
              Solicitar un nuevo enlace de restablecimiento
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
        <h2 className="text-2xl font-bold text-center mb-6 text-domired-600">Restablecer Contraseña</h2>
        
        {email && (
          <p className="text-center mb-4 text-gray-600 dark:text-gray-300">
            Establece una nueva contraseña para <span className="font-medium">{email}</span>
          </p>
        )}
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        {message && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4" role="alert">
            <span className="block sm:inline">{message}</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">
              Nueva Contraseña
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700"
              required
            />
          </div>
          
          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium mb-1">
              Confirmar Contraseña
            </label>
            <input
              type="password"
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700"
              required
            />
          </div>
          
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <p>La contraseña debe tener al menos:</p>
            <ul className="list-disc pl-5 mt-1">
              <li>8 caracteres</li>
              <li>Una letra</li>
              <li>Un número</li>
            </ul>
          </div>
          
          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-domired-600 hover:bg-domired-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 disabled:opacity-50"
            >
              {loading ? 'Restableciendo...' : 'Restablecer Contraseña'}
            </button>
          </div>
        </form>
        
        <div className="mt-4 text-center">
          <Link to="/login" className="text-sm font-medium text-domired-600 hover:text-domired-500">
            Volver a Iniciar Sesión
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
